import React, { useEffect, useState } from "react";
import { PlayCircle, BookOpen, GraduationCap } from "lucide-react";
import AboutBannerImage1 from "../../src/assets/images/dna1.png";
import AboutBannerImage2 from "../../src/assets/images/aboutbanner2.jpeg";
import AboutBannerImage3 from "../../src/assets/images/dna1.png";
import AboutBannerImage4 from "../../src/assets/images/aboutbanner2.jpeg";
import { BarChart3, CheckSquare, Target, Star } from "lucide-react";
import DashboardImage from "../../src/assets/images/aboutbanner2.jpeg";
import { Database, ShieldCheck, PieChart, Smartphone } from "lucide-react";
import { Activity, Heart, Briefcase, Smile } from "lucide-react";
import { getTestimonial, getInstructors } from "../api/api";

const audience = [
    {
        id: 1,
        title: "Fitness Enthusiasts",
        icon: <Activity className="w-4 h-4 text-gray-500" />,
    },
    {
        id: 2,
        title: "Nutrition Students",
        icon: <BookOpen className="w-4 h-4 text-gray-500" />,
    },
    {
        id: 3,
        title: "Health Coaches",
        icon: <Heart className="w-4 h-4 text-gray-500" />,
    },
    {
        id: 4,
        title: "Wellness Professionals",
        icon: <Briefcase className="w-4 h-4 text-gray-500" />,
    },
];

const platformFeatures = [
    {
        id: 1,
        title: "Scalable CMS",
        description:
            "Robust course management system built for high-volume content delivery.",
        icon: <Database className="w-6 h-6 text-gray-300" />,
    },
    {
        id: 2,
        title: "Secure Enrollment",
        description:
            "Bank-grade secure payment gateways and encrypted user enrollment.",
        icon: <ShieldCheck className="w-6 h-6 text-gray-300" />,
    },
    {
        id: 3,
        title: "Deep Analytics",
        description:
            "Comprehensive user analytics, engagement tracking, and learning reports.",
        icon: <PieChart className="w-6 h-6 text-gray-300" />,
    },
    {
        id: 4,
        title: "Omnichannel Sync",
        description:
            "Seamlessly integrates with the entire Nutrillion ecosystem.",
        icon: <Smartphone className="w-6 h-6 text-gray-300" />,
    },
];

const tools = [
    {
        id: 1,
        title: "Recorded Courses",
        description:
            "Structured video-based learning paths let users move at their own pace while revisiting core lessons, expert walkthroughs, and practical nutrition concepts anytime.",
        icon: <PlayCircle className="text-green-500 w-6 h-6" />,
        border: "border-green-500",
    },
    {
        id: 2,
        title: "Digital Library",
        description:
            "Give learners instant access to diet guides, wellness playbooks, evidence-based reading materials, meal planning resources, and curated educational references.",
        icon: <BookOpen className="text-purple-500 w-6 h-6" />,
        border: "border-purple-500",
    },
    {
        id: 3,
        title: "Certification Programs",
        description:
            "Completion-based certifications help learners validate progress, showcase achievements, and build stronger personal or professional credibility in nutrition and wellness.",
        icon: <GraduationCap className="text-green-500 w-6 h-6" />,
        border: "border-green-500",
    },
];

const faculty = [
    {
        id: 1,
        name: "Dr. Sarah Jenkins",
        role: "Clinical Nutritionist",
        description:
            "Ph.D. in Nutritional Sciences with 15+ years of clinical experience.",
        image: AboutBannerImage1,
    },
    {
        id: 2,
        name: "Marcus Thorne, RD",
        role: "Sports Dietitian",
        description:
            "Works with Olympic athletes and specializes in performance nutrition.",
        image: AboutBannerImage2,
    },
    {
        id: 3,
        name: "Priya Patel, MSc",
        role: "Holistic Wellness Coach",
        description:
            "Expert in plant-based diets, gut health, and mindful eating practices.",
        image: AboutBannerImage3,
    },
    {
        id: 4,
        name: "Dr. Robert Vance",
        role: "Metabolic Specialist",
        description:
            "Leading researcher in metabolic disorders and therapeutic diets.",
        image: AboutBannerImage4,
    },
];

const features = [
    {
        id: 1,
        text: "Course progress tracking & completion metrics",
        icon: <BarChart3 className="text-green-500 w-5 h-5" />,
    },
    {
        id: 2,
        text: "Detailed overview of completed lessons & modules",
        icon: <CheckSquare className="text-green-500 w-5 h-5" />,
    },
    {
        id: 3,
        text: "Quiz scores, assessments, and performance history",
        icon: <Target className="text-green-500 w-5 h-5" />,
    },
    {
        id: 4,
        text: "AI-driven recommended courses based on interests",
        icon: <Star className="text-green-500 w-5 h-5" />,
    },
];

const CoursesLearningTools = () => {
    const [testimonials, setTestimonials] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [instructors, setInstructors] = useState([]);
    const [instructorsLoading, setInstructorsLoading] = useState(true);
    const [instructorsError, setInstructorsError] = useState(null);

    useEffect(() => {
        const fetchTestimonials = async () => {
            try {
                setLoading(true);
                // Pass "course" as type parameter to filter at API level
                const response = await getTestimonial("course");
                console.log("API Response:", response);

                if (response && response.status === true) {
                    // Map the API data to match component structure
                    const mappedTestimonials = response.data.map(item => ({
                        id: item.id,
                        text: item.description?.replace(/<[^>]*>/g, ''), // Plain text version
                        name: item.name || item.title,
                        role: item.designation,
                        image: item.imageUrl,
                        description: item.description // Keep HTML for rendering
                    }));

                    setTestimonials(mappedTestimonials);
                    console.log("Mapped Testimonials:", mappedTestimonials);
                    setError(null);
                } else {
                    console.error("Failed to load testimonials:", response?.message);
                    setTestimonials([]);
                    setError(response?.message || "No testimonials found");
                }
            } catch (error) {
                console.error("Error fetching testimonials:", error);
                setTestimonials([]);
                setError(error.message);
            } finally {
                setLoading(false);
            }
        };

        fetchTestimonials();
    }, []);

    useEffect(() => {
        const fetchInstructors = async () => {
            try {
                setInstructorsLoading(true);
                const response = await getInstructors();

                if (response && response.success === true) {
                    const mappedInstructors = response.instructors.map(item => ({
                        id: item.id,
                        name: item.name,
                        role: item.title,
                        bio: item.bio,
                        image: item.profile_image,
                        credentials: item.credentials
                    }));
                    setInstructors(mappedInstructors);
                }
            } catch (error) {
                setInstructorsError(error.message);
            } finally {
                setInstructorsLoading(false);
            }
        };
        fetchInstructors();
    }, []);

    const truncateBio = (bio, maxLength = 120) => {
        if (!bio) return "";
        if (bio.length <= maxLength) return bio;
        return bio.substring(0, maxLength) + "...";
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500"></div>
            </div>
        );
    }

    return (
        <>
            <div className="bg-white py-16 px-6">
                {/* Heading */}
                <div className="text-center mb-12">
                    <p className="text-green-500 text-sm tracking-widest font-semibold mb-2 font-lato">
                        INTERACTIVE LEARNING
                    </p>
                    <h2 className="text-3xl md:text-4xl font-semibold text-gray-800 font-merriweather">
                        Focused Learning Tools for Better Outcomes
                    </h2>
                    <p className="text-gray-500 mt-3 font-lato">
                        Nutrition Academy keeps the learning experience simple and premium with three essential pillars that help users study, access trusted resources, and earn meaningful credentials.
                    </p>
                </div>

                {/* Cards */}
                <div className="custom-padding xl-screen-padding grid grid-cols-1 md:grid-cols-3 gap-8">
                    {tools.map((tool) => (
                        <div
                            key={tool.id}
                            className={`bg-[#f9f9f9] p-6 rounded-xl shadow-sm border-t-4 ${tool.border}`}
                        >
                            {/* Icon */}
                            <div className="mb-4">
                                {tool.icon}
                            </div>

                            {/* Title */}
                            <h3 className="font-semibold text-gray-800 mb-2 font-lato">
                                {tool.title}
                            </h3>

                            {/* Description */}
                            <p className="text-sm text-gray-500 leading-relaxed font-lato">
                                {tool.description}
                            </p>
                        </div>
                    ))}
                </div>
            </div>

            <div className="bg-[#f7f7f7] py-16 px-6">
                <div className="text-center mb-12">
                    <p className="text-green-500 text-sm tracking-widest font-semibold mb-2">
                        WORLD-CLASS FACULTY
                    </p>
                    <h2 className="text-3xl md:text-4xl text-gray-800 font-merriweather">
                        Learn from the Best in Nutrition
                    </h2>
                    <p className="text-gray-500 mt-3 font-lato">
                        Our courses are taught by recognized doctors, certified dietitians, and wellness experts.
                    </p>
                </div>

                {!instructorsLoading && instructors.length > 0 ? (
                    <div className="custom-padding xl-screen-padding grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-10 text-center">
                        {instructors.map((member) => (
                            <div key={member.id}>
                                <div className="flex justify-center mb-4">
                                    <img
                                        src={member.image || "https://via.placeholder.com/128x128?text=Instructor"}
                                        alt={member.name}
                                        className="w-32 h-32 object-cover rounded-full border-4 border-white shadow-md"
                                        onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src = "https://via.placeholder.com/128x128?text=Instructor";
                                        }}
                                    />
                                </div>

                                <h3 className="font-semibold text-gray-800 font-lato">
                                    {member.name}
                                </h3>

                                <p className="text-sm text-gray-500 mb-2 font-lato">
                                    {member.role}
                                </p>

                                <p className="text-sm text-gray-400 font-lato line-clamp-2">
                                    {truncateBio(member.bio)}
                                </p>
                            </div>
                        ))}
                    </div>
                ) : (
                    !instructorsLoading && (
                        <div className="text-center py-8">
                        </div>
                    )
                )}

                {instructorsError && (
                    <div className="text-center py-8">
                    </div>
                )}
            </div>

            <div className="bg-white py-16 px-6">
                <div className="custom-padding xl-screen-padding grid md:grid-cols-2 gap-12 items-center">
                    <div>
                        <p className="text-[#7c517e] text-sm tracking-widest font-semibold mb-2 font-lato">
                            PERSONALIZED EXPERIENCE
                        </p>

                        <h2 className="text-3xl md:text-4xl text-gray-800 leading-snug font-merriweather">
                            Your Custom Learning Dashboard
                        </h2>

                        <p className="text-gray-500 mt-4 mb-6 font-lato">
                            Every user gets a personalized portal to track their educational
                            journey, manage achievements, and discover new growth opportunities
                            within the Nutrillion ecosystem.
                        </p>

                        <div className="space-y-3">
                            {features.map((item) => (
                                <div
                                    key={item.id}
                                    className="flex items-center gap-3 bg-gray-100 font-semibold px-4 py-3 rounded-lg font-lato"
                                >
                                    {item.icon}
                                    <span className="text-sm text-gray-700">{item.text}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="relative">
                        <img
                            src={DashboardImage}
                            alt="Dashboard Preview"
                            className="rounded-xl shadow-md w-full object-cover"
                        />

                        <div className="absolute bottom-[-20px] right-[-1.5rem] bg-white shadow-lg rounded-sm px-6 py-4 flex gap-8">
                            <div className="text-center">
                                <p className="text-2xl font-bold text-green-500 font-merriweather">12</p>
                                <p className="text-xs text-gray-500 font-semibold">COURSES</p>
                            </div>

                            <div className="text-center">
                                <p className="text-2xl font-bold text-green-500 font-merriweather">3</p>
                                <p className="text-xs text-gray-500 font-semibold">CERTIFICATES</p>
                            </div>

                            <div className="text-center">
                                <p className="text-2xl font-bold text-green-500 font-merriweather">95%</p>
                                <p className="text-xs text-gray-500 font-semibold">AVG. SCORE</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="bg-[#1f2a37] py-20 px-6 text-center">
                {/* Heading */}
                <p className="text-green-400 text-sm tracking-widest font-semibold mb-2 font-lato">
                    PHASE 2 TECHNOLOGY
                </p>

                <h2 className="text-3xl md:text-4xl font-semibold text-white font-merriweather">
                    Advanced Platform Capabilities
                </h2>

                <p className="text-gray-400 mt-4 font-lato">
                    Built for scale, security, and seamless integration with existing
                    Nutrillion Web & Mobile Apps.
                </p>

                {/* Cards */}
                <div className="custom-padding xl-screen-padding grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 mt-12">
                    {platformFeatures.map((item) => (
                        <div
                            key={item.id}
                            className="bg-[#2b3645] p-6 rounded-xl border border-gray-700 hover:border-gray-500 transition"
                        >
                            {/* Icon */}
                            <div className="w-12 h-12 flex items-center justify-center bg-[#3a4554] rounded-full mb-4 mx-auto">
                                {item.icon}
                            </div>

                            {/* Title */}
                            <h3 className="text-white font-semibold mb-2 font-lato">
                                {item.title}
                            </h3>

                            {/* Description */}
                            <p className="text-sm text-gray-400 font-lato">
                                {item.description}
                            </p>
                        </div>
                    ))}
                </div>
            </div>

            {/* Success Stories Section - Using API Data */}
            {!loading && testimonials.length > 0 && (
                <div className="bg-[#f4f5f7] py-20 px-6">
                    {/* Heading */}
                    <div className="text-center mb-14">
                        <p className="text-green-500 text-sm tracking-widest font-semibold mb-2">
                            SUCCESS STORIES
                        </p>

                        <h2 className="text-3xl md:text-4xl font-semibold text-gray-800 font-merriweather">
                            Transformations Start Here
                        </h2>

                        <p className="text-gray-500 mt-4 font-lato">
                            Hear from students and professionals who have elevated their careers and
                            lives through Nutrillion Academy.
                        </p>
                    </div>

                    {/* Cards */}
                    <div className="custom-padding xl-screen-padding grid grid-cols-1 md:grid-cols-3 gap-8">
                        {testimonials.map((item) => (
                            <div
                                key={item.id}
                                className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 relative"
                            >
                                {/* Stars */}
                                <div className="flex gap-1 mb-4 text-yellow-400 font-lato">
                                    {[...Array(5)].map((_, i) => (
                                        <Star key={i} size={16} fill="currentColor" />
                                    ))}
                                </div>

                                {/* Quote - Using dangerouslySetInnerHTML for HTML content */}
                                <div
                                    className="text-gray-600 text-sm leading-relaxed mb-6 font-lato"
                                    dangerouslySetInnerHTML={{ __html: item.description }}
                                />

                                {/* User */}
                                <div className="flex items-center gap-3">
                                    <img
                                        src={item.image}
                                        alt={item.name}
                                        className="w-10 h-10 rounded-full object-cover"
                                        onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src = "https://via.placeholder.com/40";
                                        }}
                                    />

                                    <div>
                                        <p className="text-sm font-extrabold text-gray-800 font-lato">
                                            {item.name}
                                        </p>
                                        <p className="text-xs text-gray-500 font-lato">{item.role}</p>
                                    </div>
                                </div>

                                {/* Quote Icon */}
                                <span className="absolute top-4 right-6 text-gray-200 text-5xl font-serif">
                                    ”
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Show message if no testimonials */}
            {!loading && testimonials.length === 0 && !error && (
                <div className="bg-[#f4f5f7] py-20 px-6">
                    <div className="text-center">
                        <p className="text-gray-500">No testimonials available at the moment.</p>
                    </div>
                </div>
            )}

            {/* Show error message */}
            {error && (
                <div className="bg-[#f4f5f7] py-20 px-6">
                    <div className="text-center">
                        <p className="text-red-500">Error loading testimonials: {error}</p>
                    </div>
                </div>
            )}

            <div className="bg-white py-20 px-6">
                <div className="text-center mb-12 custom-padding xl-screen-padding">
                    <p className="text-green-500 text-sm tracking-widest font-semibold mb-2">
                        YOUR GROWTH PARTNER
                    </p>

                    <h2 className="text-3xl md:text-4xl text-gray-800 font-merriweather">
                        Who Is Nutrillion Academy For?
                    </h2>

                    <p className="text-gray-500 mt-4 mx-auto font-lato">
                        Whether you're starting your wellness journey or expanding your
                        professional expertise.
                    </p>
                </div>

                {/* Tags */}
                <div className="flex flex-wrap justify-center gap-4 mx-auto">
                    {audience.map((item) => (
                        <div
                            key={item.id}
                            className="flex items-center gap-2 px-5 py-3 bg-white border border-gray-200 rounded-lg shadow-sm text-sm font-semibold text-gray-700 hover:shadow-md transition font-lato font-semibold"
                        >
                            {item.icon}
                            {item.title}
                        </div>
                    ))}
                </div>

                {/* Highlight Box */}
                <div className="flex justify-center mt-8">
                    <div className="flex items-center gap-2 px-6 py-4 border border-green-400 text-black rounded-xl text-sm font-semibold">
                        <Smile className="w-4 h-4" />
                        Anyone improving their lifestyle
                    </div>
                </div>
            </div>
        </>
    );
};

export default CoursesLearningTools;